import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShowChanges } from 'global/hooks/useShowChanges';
import { getUrlParams } from 'global/utils';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { first, get } from 'global/lib/lodash';

export const withDocumentParent = (DocumentComponent) => {
    return (props) => {
        const { doc: editionDocument, documentParentId, edition, printView } = props;
        const { showChangesOn, showChangesId } = useShowChanges();
        const loca = useLocation();
        const params = getUrlParams(loca.search);

        // the "Document Parent" is for rendering changes; the parent ID can be passed through in one of 3 ways (in priority order):
        //   1. props.documentParentId: If printing items, we pass the parentId as a prop to the document, and ignore other methods
        //   2. URL Params: after a user clicked "Show Changes" on an Edition list and then clicks on a list-item
        //      it puts the parent ID in the URL
        //   3. showChangesId: If a user navigates to a document first, and _then_ clicks "Show Changes",
        //      we pass the parent ID through the useShowChanges hook so as to not have to re-render the URL
        const [_documentParentId, setDocumentParentId] = useState(printView ? documentParentId : (get(params, 'documentParentId') ?? showChangesId));
        useEffect(() => {
            // if showChanges id changes and we don't have a better id, use it.
            if (!printView && !documentParentId) {
                if (showChangesId) {
                    setDocumentParentId(showChangesId);
                }
                else {
                    setDocumentParentId(false)
                }
            }
        }, [showChangesId]);

        const editionParentId = get(edition, 'data.parentId');
        const editionParent = useRetrieveData(
            { url: `/editions/${editionParentId}` },
            { build: ['editions'], waitFor: !!editionParentId && showChangesOn, useEffect: true, transformData: (d) => first(d), useCache: { reload: 20 } }
        );
        
        const isSBS = editionDocument?.documentableType === 'step_by_step';
        const parentDocument = useRetrieveData(
            { url: isSBS ? `/step-by-steps/${_documentParentId}` : `/checklists/${_documentParentId}` },
            {
                useEffect: true,
                useLangFilter: true,
                build: isSBS ? ['stepBySteps'] : ['checklists'],
                params: {
                    include: isSBS ? 'items,images,items.images' : 'checkpoints.checkpointOptions,checkpoints.images,checkpoints.comments,classification.system,tradeTypes',
                },
                waitFor: showChangesOn && !!_documentParentId,
                transformData: (d) => first(d),
                useCache: { reload: 20 },
            }
        );
        return <DocumentComponent {...props} documentParentId={_documentParentId} parentDocument={parentDocument} editionParent={editionParent}></DocumentComponent>;
    }
}

export default withDocumentParent;